import React, { useState, FC, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Coffee, PlayCircle, ChevronDown, PhoneIncoming, PhoneOutgoing, Timer,
  LogOut, ArrowRight, ShieldCheck, Clock, Layers, PhoneCall, RotateCcw,
  Activity, X, Users, PhoneMissed, CheckCircle, XCircle, Lock, Headset, PauseCircle,
  Sparkles, Radio, Zap, Flame, Award
} from 'lucide-react';
import axios from 'axios';
import mqtt, { MqttClient } from 'mqtt';
import clsx from 'clsx';
import { ListWrapper } from '../../modules/apps/ticket/components/ticket-list/List';
import { CampaignDetailInfo } from '../../modules/apps/call/campaign-list/form/CampaignDetailInfo';
import { useThemeMode } from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';
import { useAgentContext } from '../../modules/apps/call/AgentContext';
import { useAuth } from '../../modules/auth';

const AgentDashboard: FC = () => {
  useEffect(() => {
    document.body.setAttribute('data-kt-app-sidebar-minimize', 'on');
    return () => {
      document.body.removeAttribute('data-kt-app-sidebar-minimize');
    };
  }, []);

  const { currentUser } = useAuth();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark' || document.documentElement.getAttribute('data-bs-theme') === 'dark';

  const [triggerPopData, setTriggerPopData] = useState<{ phone: string, timestamp: number } | null>(null);
  const lastStatusRef = useRef<string>("");

  const {
    isLoggedIn, isAux, selectedCampaign, setSelectedCampaign,
    campaigns, auxReasons, seconds, handleLogin, handleLogout,
    handleAuxAction, agentExt, token,
    data, fullCampaignDetail, activeCampaignData, campaignMetrics, auxReasonName,
    isCampaignDetailLoading
  } = useAgentContext();

  const [activeTicket, setActiveTicket] = useState<any>(null);
  const clientRef = useRef<MqttClient | null>(null);

  const clearTrigger = useCallback(() => {
    setTriggerPopData(null);
  }, []);

  useEffect(() => {
    const MQTT_URL = process.env.REACT_APP_MQTT_URL;
    if (isLoggedIn && agentExt) {
      const client = mqtt.connect(`wss://${MQTT_URL}`, {
        clientId: `AgentDash_${agentExt}_${Math.random().toString(16).slice(2, 8)}`,
        username: 'user_asterisk',
        password: 'password_rahasia',
        clean: true,
        reconnectPeriod: 2000,
      });

      clientRef.current = client;

      client.on('connect', () => {
        client.subscribe(`dashboard/ext_status/${agentExt}`);
      });

      client.on('message', (_topic, payload) => {
        try {
          const rawData = JSON.parse(payload.toString());
          const currentStatus = rawData.status;
          const currentCaller = rawData.caller;
          const prevStatus = lastStatusRef.current;

          if (currentStatus === "UP" && prevStatus !== "UP" && currentCaller) {
            setTriggerPopData({
              phone: currentCaller,
              timestamp: Date.now()
            });
            setActiveTicket(null);
          }

          if (currentStatus === "RINGING" && prevStatus !== "RINGING" && currentCaller) {
            setActiveTicket({
              callerNumber: currentCaller,
              display: rawData.display || 'Incoming Call'
            });
          }

          if (currentStatus === "IDLE" && prevStatus !== "IDLE") {
            setActiveTicket(null);
          }

          lastStatusRef.current = currentStatus;
        } catch (err) {
          console.error('MQTT Parsing Error', err);
        }
      });

      return () => { if (client) client.end(true); };
    }
  }, [isLoggedIn, agentExt]);

  const ScreenPop = () => {
    if (!activeTicket) return null;
    return (
      <div className="position-fixed bottom-0 end-0 m-6 m-md-10 animate__animated animate__bounceInRight" style={{ zIndex: 9999, width: '360px' }}>
        <div className="card shadow-lg border-0 rounded-4 overflow-hidden" 
          style={{ 
            background: 'linear-gradient(135deg, #1e1e2d 0%, #2a2a3c 100%)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.1)'
          }}>
          <div className="card-body p-6 text-white">
            <div className="d-flex align-items-center justify-content-between mb-4">
              <div className="d-flex align-items-center gap-3">
                <div className="p-3 rounded-circle bg-success bg-opacity-25 text-success d-flex align-items-center justify-content-center animate__animated animate__pulse animate__infinite">
                  <PhoneIncoming size={22} />
                </div>
                <div>
                  <h5 className="fw-bolder m-0 text-white">Panggilan Masuk</h5>
                  <span className="text-gray-400 fs-8">Ext: {agentExt}</span>
                </div>
              </div>
              <button className="btn btn-icon btn-sm btn-color-gray-400 btn-active-color-white" onClick={() => setActiveTicket(null)}>
                <X size={18} />
              </button>
            </div>

            <div className="bg-dark bg-opacity-50 rounded-3 p-4 mb-4 text-center border border-secondary border-opacity-25">
              <span className="text-gray-400 fs-9 fw-bold text-uppercase d-block mb-1">Nomor Nasabah</span>
              <h2 className="fw-bolder text-white m-0 font-monospace letter-spacing-1">{activeTicket.callerNumber}</h2>
            </div>

            <button 
              className="btn btn-success w-100 fw-bold py-3 rounded-3 d-flex align-items-center justify-content-center gap-2 shadow-sm"
              onClick={() => {
                setTriggerPopData({ phone: activeTicket.callerNumber, timestamp: Date.now() });
                setActiveTicket(null);
              }}
            >
              <CheckCircle size={18} />
              <span>Buka Data Nasabah</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const OfflineLaunchpad = () => {
    return (
      <div className="d-flex align-items-center justify-content-center min-vh-75 py-10">
        <div className="card border-0 shadow-lg rounded-4 overflow-hidden w-100" style={{ maxWidth: '640px' }}>
          {/* Header Gradient */}
          <div className="p-8 text-white text-center position-relative"
            style={{
              background: 'linear-gradient(135deg, #005AAA 0%, #0077dd 50%, #4338ca 100%)',
            }}>
            <div className="d-inline-flex p-4 rounded-circle bg-white bg-opacity-15 shadow-sm mb-4">
              <Headset size={36} className="text-white" />
            </div>
            <h2 className="fw-boldest text-white fs-2 mb-1">
              Selamat Datang, {currentUser?.first_name || currentUser?.name || 'Agent'}!
            </h2>
            <p className="text-white text-opacity-80 fs-6 mb-0">
              Extension: <span className="fw-bold font-monospace bg-white bg-opacity-20 px-2 py-0.5 rounded">{agentExt || currentUser?.extension || '-'}</span> • Status: <span className="badge badge-light-warning fw-bold">Offline</span>
            </p>
          </div>

          {/* Body Form */}
          <div className="card-body p-8 p-lg-10 bg-body">
            <label className="fs-6 fw-bold text-gray-800 mb-3 d-block">
              Pilih Antrean / Campaign Shift Hari Ini:
            </label>

            <div className="d-flex flex-column gap-3 mb-8">
              {campaigns.map((c: any, i: number) => {
                const cName = c.name || c.Name;
                const isSelected = selectedCampaign === cName;
                const isInbound = cName === 'inbound';
                return (
                  <div
                    key={c.id || i}
                    onClick={() => setSelectedCampaign(cName)}
                    className={clsx(
                      "p-4 rounded-3 border-2 border cursor-pointer d-flex align-items-center justify-content-between transition-all hover-elevate-up",
                      isSelected ? "border-primary bg-light-primary shadow-xs" : "border-gray-200 bg-body hover-border-primary"
                    )}
                    style={{ animationDelay: `${0.2 + (i * 0.1)}s` }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div className={clsx(
                        "p-2.5 rounded-circle d-flex align-items-center justify-content-center",
                        isInbound ? "bg-light-success text-success" : "bg-light-primary text-primary"
                      )}>
                        {isInbound ? <PhoneIncoming size={18} /> : <Zap size={18} />}
                      </div>
                      <div>
                        <div className="fw-bold text-gray-900 fs-6">{cName}</div>
                        <span className="text-muted fs-8">
                          {isInbound ? 'Layanan Panggilan Masuk & Tiket Customer Service' : `Mode: ${c.dialMode || 'Outbound'} Campaign`}
                        </span>
                      </div>
                    </div>

                    <div className="d-flex align-items-center">
                      <div className={clsx(
                        "rounded-circle d-flex align-items-center justify-content-center",
                        isSelected ? "bg-primary text-white" : "border border-gray-400"
                      )} style={{ width: 20, height: 20 }}>
                        {isSelected && <div className="rounded-circle bg-white" style={{ width: 8, height: 8 }}></div>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Launch Button */}
            <button
              className="btn btn-primary w-100 py-4 fs-5 fw-bold rounded-3 shadow-md d-flex align-items-center justify-content-center gap-3 transition-all hover-elevate-up"
              style={{
                background: 'linear-gradient(135deg, #005AAA 0%, #0077dd 100%)',
                animationDelay: `${0.3 + (campaigns.length * 0.1)}s`
              }}
              onClick={handleLogin}
            >
              <Sparkles size={20} />
              <span>Mulai Shift & Siap Menerima Panggilan</span>
              <ArrowRight size={20} />
            </button>

            <div className="text-center mt-4">
              <span className="text-muted fs-8">
                Setelah login, softphone Anda akan otomatis terhubung ke sistem antrean Asterisk.
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };



  return (
    <div className="min-vh-100 w-100 bg-body">
      <ScreenPop />

      <div className="container-fluid px-4 px-lg-8 pb-8 pt-2">
        {!isLoggedIn ? (
          <OfflineLaunchpad />
        ) : (
          <div className="w-100">
            {/* Main Content Workspace */}
            <div className="position-relative">
              {(campaigns.length === 0 || (isCampaignDetailLoading && selectedCampaign !== 'inbound')) ? (
                <div className="card shadow-sm border-0 rounded-4 overflow-hidden p-10 text-center" style={{ minHeight: '50vh' }}>
                  <div className="d-flex flex-column align-items-center justify-content-center h-100">
                    <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}></div>
                    <div className="text-muted fw-semibold fs-5">Menyiapkan workspace Anda...</div>
                  </div>
                </div>
              ) : (selectedCampaign === 'inbound') ? (
                <div className="card shadow-sm border-0 rounded-4 overflow-hidden">
                  <ListWrapper
                    externalTicketTrigger={triggerPopData}
                    onTriggerProcessed={clearTrigger}
                  />
                </div>
              ) : (!fullCampaignDetail) ? (
                <div className="card shadow-sm border-0 rounded-4 overflow-hidden p-10 text-center" style={{ minHeight: '50vh' }}>
                  <div className="d-flex flex-column align-items-center justify-content-center h-100">
                    <div className="text-danger mb-3">
                      <XCircle size={48} />
                    </div>
                    <div className="text-muted fw-semibold fs-5">Gagal memuat detail Campaign. Silakan muat ulang halaman.</div>
                  </div>
                </div>
              ) : (
                <div className="rounded-4">
                  <CampaignDetailInfo
                    data={fullCampaignDetail}
                    externalTicketTrigger={triggerPopData}
                    onTriggerProcessed={clearTrigger}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentDashboard;